import React from 'react';
import { AppCheckbox } from '@/components/atoms';
import { CollapsibleCard } from '@/components/molecules';
import { RA2NDatePicker } from '@/components/organisms';
import type { PrintOptionsState } from '../types';

interface Props {
  /** BD SCR-182 §4.5/§4.6 name the practitioner 施術師; A2 says はり・きゅう師. */
  isR2: boolean;
  printOptions: PrintOptionsState;
  setPrintOptions: React.Dispatch<React.SetStateAction<PrintOptionsState>>;
  printTargetsDisabled: boolean;
  monthlyDisabled: boolean;
}

const PrintOptionsPanel: React.FC<Props> = ({
  isR2,
  printOptions,
  setPrintOptions,
  printTargetsDisabled,
  monthlyDisabled,
}) => {
  // BD §3: 10割 is locked only by the search term type, like its form. It applies
  // together with a form, so ticking it also selects that form.
  return (
    <CollapsibleCard title="印刷オプション" className="mb-3">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[360px_1fr]">
        <div className="rounded border border-blue-300 p-3">
          <div className="mb-3 text-sm font-semibold">帳票種類</div>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-[1fr_auto] items-center gap-4">
              <AppCheckbox
                checked={printOptions.print_visit_clinic_invoice}
                onChange={e => {
                  const checked = e.target.checked;
                  setPrintOptions(prev => ({
                    ...prev,
                    print_visit_clinic_invoice: checked,
                    print_visit_clinic_invoice_100: checked
                      ? prev.print_visit_clinic_invoice_100
                      : false,
                  }));
                }}
                disabled={printTargetsDisabled}
              >
                領収書
              </AppCheckbox>
              <AppCheckbox
                checked={printOptions.print_visit_clinic_invoice_100}
                onChange={e => {
                  const checked = e.target.checked;
                  setPrintOptions(prev => ({
                    ...prev,
                    print_visit_clinic_invoice_100: checked,
                    print_visit_clinic_invoice: checked ? true : prev.print_visit_clinic_invoice,
                  }));
                }}
                disabled={printTargetsDisabled}
              >
                10割
              </AppCheckbox>
            </div>

            <div className="grid grid-cols-[1fr_auto] items-center gap-4">
              <AppCheckbox
                checked={printOptions.print_visit_clinic_invoice_free_medical}
                onChange={e => {
                  const checked = e.target.checked;
                  setPrintOptions(prev => ({
                    ...prev,
                    print_visit_clinic_invoice_free_medical: checked,
                    print_visit_clinic_invoice_free_medical_100: checked
                      ? prev.print_visit_clinic_invoice_free_medical_100
                      : false,
                  }));
                }}
                disabled={printTargetsDisabled}
              >
                保険外明細
              </AppCheckbox>
              <AppCheckbox
                checked={printOptions.print_visit_clinic_invoice_free_medical_100}
                onChange={e => {
                  const checked = e.target.checked;
                  setPrintOptions(prev => ({
                    ...prev,
                    print_visit_clinic_invoice_free_medical_100: checked,
                    print_visit_clinic_invoice_free_medical: checked
                      ? true
                      : prev.print_visit_clinic_invoice_free_medical,
                  }));
                }}
                disabled={printTargetsDisabled}
              >
                10割
              </AppCheckbox>
            </div>

            <div className="grid grid-cols-[1fr_auto] items-center gap-4">
              <AppCheckbox
                checked={printOptions.print_monthly}
                onChange={e => {
                  const checked = e.target.checked;
                  setPrintOptions(prev => ({
                    ...prev,
                    print_monthly: checked,
                    print_monthly_100: checked ? prev.print_monthly_100 : false,
                  }));
                }}
                disabled={monthlyDisabled}
              >
                月別
              </AppCheckbox>
              <AppCheckbox
                checked={printOptions.print_monthly_100}
                onChange={e => {
                  const checked = e.target.checked;
                  setPrintOptions(prev => ({
                    ...prev,
                    print_monthly_100: checked,
                    print_monthly: checked ? true : prev.print_monthly,
                  }));
                }}
                disabled={monthlyDisabled}
              >
                10割
              </AppCheckbox>
            </div>
          </div>
        </div>

        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            <AppCheckbox
              checked={printOptions.option_7}
              onChange={e => setPrintOptions(prev => ({ ...prev, option_7: e.target.checked }))}
            >
              治療院基本情報の治療院名を表示
            </AppCheckbox>
            <AppCheckbox
              checked={printOptions.option_8}
              onChange={e => setPrintOptions(prev => ({ ...prev, option_8: e.target.checked }))}
            >
              治療院基本情報の住所と電話番号を表示
            </AppCheckbox>
          </div>

          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            <AppCheckbox
              checked={printOptions.option_1}
              onChange={e => setPrintOptions(prev => ({ ...prev, option_1: e.target.checked }))}
            >
              施術管理者の名前を表示
            </AppCheckbox>
            <AppCheckbox
              checked={printOptions.option_2}
              onChange={e => setPrintOptions(prev => ({ ...prev, option_2: e.target.checked }))}
            >
              施術管理者の住所・電話番号を表示
            </AppCheckbox>
          </div>

          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            <AppCheckbox
              checked={printOptions.option_3}
              onChange={e => setPrintOptions(prev => ({ ...prev, option_3: e.target.checked }))}
            >
              {isR2 ? '施術師の名前を表示' : 'はり・きゅう師の名前を表示'}
            </AppCheckbox>
            <AppCheckbox
              checked={printOptions.option_4}
              onChange={e => setPrintOptions(prev => ({ ...prev, option_4: e.target.checked }))}
            >
              {isR2
                ? '施術師拠点の住所・電話番号を表示'
                : 'はり・きゅう師拠点の住所・電話番号を表示'}
            </AppCheckbox>
          </div>

          <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1fr_1fr] items-center">
            <AppCheckbox
              checked={printOptions.option_11}
              onChange={e => setPrintOptions(prev => ({ ...prev, option_11: e.target.checked }))}
            >
              受診者番号を表示
            </AppCheckbox>
            {/* BD 4.1 §4 item 4.9 / legacy A2 visit_clinic_invoices: no 施術報告書交付料 option, the fee always prints. */}
            {!isR2 && <span className="text-gray-500">※施術報告書交付料は未対応です。</span>}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <AppCheckbox
              checked={printOptions.option_5}
              onChange={e => setPrintOptions(prev => ({ ...prev, option_5: e.target.checked }))}
            >
              日付を表示
            </AppCheckbox>
            <RA2NDatePicker
              value={printOptions.option_date || undefined}
              onChange={v =>
                setPrintOptions(prev => ({
                  ...prev,
                  option_date: v || '',
                  option_5: v ? true : prev.option_5,
                }))
              }
              placeholder="元号00年00月00日"
              picker="day"
              startDate="1980-01-01"
              endDate="2057-12-31"
              disabled={!printOptions.option_5}
            />
          </div>
        </div>
      </div>
    </CollapsibleCard>
  );
};

export default PrintOptionsPanel;
